import React from 'react';
import { Search, Mic, MoreVertical, Download, Share2, Trash2, Edit, FileText } from 'lucide-react';

const RecordingsTab = ({
    recordings,
    searchQuery,
    setSearchQuery,
    recordingMenuOpen,
    setRecordingMenuOpen,
    deleteRecording,
    formatTime,
    setActiveTab,
    renamingRecording,
    setRenamingRecording,
    renameRecording,
    setShowTranscriptModal,
    setSelectedRecording,
    projects = [],
    onAssignItemProject,
    embedded = false,
    onStartRecording
}) => {
    const menuRefs = React.useRef({});

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            let clickedInsideMenu = false;
            Object.values(menuRefs.current).forEach(ref => {
                if (ref && ref.contains(event.target)) {
                    clickedInsideMenu = true;
                }
            });

            if (!clickedInsideMenu) {
                setRecordingMenuOpen(null);
            }
        };

        if (recordingMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [recordingMenuOpen, setRecordingMenuOpen]);
    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                {!embedded ? (
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Audio Library</h2>
                        <p className="text-gray-600 mt-1">Your transcribed audio recordings</p>
                    </div>
                ) : (
                    <div>
                        <h3 className="text-sm font-semibold text-slate-900">Past recordings</h3>
                        <p className="text-xs text-slate-500 mt-0.5">Search, assign projects, view transcripts</p>
                    </div>
                )}
                <div className="flex items-center space-x-3">
                    <div className="relative">
                        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search recordings..."
                            className="pl-10 pr-4 py-2 bg-white/80 backdrop-blur border border-gray-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                    </div>
                </div>
            </div>

            <div className="space-y-4">
                {recordings
                    .filter(recording =>
                        recording.transcript?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        recording.category?.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map(recording => (
                        <div key={recording.id} className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-gray-200/50 shadow-sm hover:shadow-md transition-all duration-300">
                            <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center">
                                    <Mic className="w-6 h-6 text-white" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-start justify-between mb-3">
                                        <div>
                                            {renamingRecording === recording.id ? (
                                                <input
                                                    type="text"
                                                    defaultValue={recording.name || `${recording.category?.charAt(0).toUpperCase() + recording.category?.slice(1)} Recording`}
                                                    className="font-semibold text-gray-900 text-lg bg-white border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            renameRecording(recording.id, e.target.value);
                                                        } else if (e.key === 'Escape') {
                                                            setRenamingRecording(null);
                                                        }
                                                    }}
                                                    onBlur={(e) => renameRecording(recording.id, e.target.value)}
                                                    autoFocus
                                                />
                                            ) : (
                                                <h3 className="font-semibold text-gray-900 text-lg">
                                                    {recording.name || `${recording.category?.charAt(0).toUpperCase() + recording.category?.slice(1)} Recording`}
                                                </h3>
                                            )}
                                            <p className="text-sm text-gray-600 mt-1">
                                                {new Date(recording.timestamp).toLocaleDateString()} • {formatTime(recording.duration || 0)}
                                            </p>
                                            {onAssignItemProject && projects.length > 0 && (
                                                <div className="mt-1 flex items-center gap-2 text-xs">
                                                    <span className="text-gray-500">Project</span>
                                                    <select
                                                        className="border border-gray-200 rounded-lg px-2 py-0.5 bg-white text-gray-800 max-w-[10rem]"
                                                        value={recording.projectId || ''}
                                                        onChange={(e) =>
                                                            onAssignItemProject('recording', recording.id, e.target.value || 'unassigned')
                                                        }
                                                    >
                                                        <option value="">None</option>
                                                        {projects.map((p) => (
                                                            <option key={p.id} value={p.id}>
                                                                {p.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${recording.status === 'processed'
                                                ? 'bg-green-100 text-green-700 border-green-200'
                                                : 'bg-yellow-100 text-yellow-700 border-yellow-200'
                                                }`}>
                                                {recording.status}
                                            </span>
                                            <div className="relative" ref={el => menuRefs.current[recording.id] = el}>
                                                <button
                                                    onClick={() => setRecordingMenuOpen(recordingMenuOpen === recording.id ? null : recording.id)}
                                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                                >
                                                    <MoreVertical className="w-4 h-4 text-gray-500" />
                                                </button>

                                                {recordingMenuOpen === recording.id && (
                                                    <div className="absolute right-0 bottom-4 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[9999] min-w-[140px] max-h-48 overflow-y-auto">
                                                        <button
                                                            onClick={() => {
                                                                setSelectedRecording(recording);
                                                                setShowTranscriptModal(true);
                                                                setRecordingMenuOpen(null);
                                                            }}
                                                            className="w-full text-left px-3 py-1.5 hover:bg-gray-100 text-xs flex items-center"
                                                        >
                                                            <FileText className="w-3 h-3 mr-2" />
                                                            View Transcript
                                                        </button>
                                                        <button
                                                            onClick={() => setRecordingMenuOpen(null)}
                                                            className="w-full text-left px-3 py-1.5 hover:bg-gray-100 text-xs flex items-center"
                                                        >
                                                            <Download className="w-3 h-3 mr-2" />
                                                            Download
                                                        </button>
                                                        <button
                                                            onClick={() => setRecordingMenuOpen(null)}
                                                            className="w-full text-left px-3 py-1.5 hover:bg-gray-100 text-xs flex items-center"
                                                        >
                                                            <Share2 className="w-3 h-3 mr-2" />
                                                            Share
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setRenamingRecording(recording.id);
                                                            }}
                                                            className="w-full text-left px-3 py-1.5 hover:bg-gray-100 text-xs flex items-center"
                                                        >
                                                            <Edit className="w-3 h-3 mr-2" />
                                                            Rename
                                                        </button>
                                                        <button
                                                            onClick={() => deleteRecording(recording.id)}
                                                            className="w-full text-left px-3 py-1.5 hover:bg-gray-100 text-xs flex items-center text-red-600"
                                                        >
                                                            <Trash2 className="w-3 h-3 mr-2" />
                                                            Delete
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {recording.insights && recording.insights.topics && (
                                        <div className="space-y-2">
                                            <h4 className="font-medium text-gray-900">Key Topics:</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {recording.insights.topics.map((topic, index) => (
                                                    <span key={index} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm">
                                                        {topic}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
            </div>

            {recordings.length === 0 && (
                <div className="text-center py-16">
                    <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <Mic className="w-12 h-12 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">No recordings yet</h3>
                    <p className="text-gray-600 mb-8">Start recording to build your audio library</p>
                    <button
                        onClick={() => setActiveTab('knowledgeCapture')}
                        className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-medium shadow-lg text-lg"
                    >
                        <Mic className="w-6 h-6 mr-3" />
                        Start Recording
                    </button>
                </div>
            )}
        </div>
    );
};

export default RecordingsTab;